<template>
  <!-- Minimal footer for booking flow on mobile -->
  <footer v-if="isBookingFlowMobile" class="bg-white border-t border-gray-200 mt-auto py-4">
    <div class="px-4 text-center">
      <div class="flex justify-center gap-4 mb-3">
        <router-link to="/contact" :class="['text-gray-600 transition-colors text-xs inline-flex items-center', linkHoverClass]">
          <i class="pi pi-send mr-1 text-xs"></i>
          Contact Us
        </router-link>
        <a :href="`mailto:${brand.footer.email}`" :class="['text-gray-600 transition-colors text-xs inline-flex items-center', linkHoverClass]">
          <i class="pi pi-envelope mr-1 text-xs"></i>
          {{ brand.footer.email }}
        </a>
      </div>
      <p class="text-gray-500 text-xs">
        © {{ currentYear }} {{ brand.footer.companyName }}. All rights reserved.
      </p>
      <!-- TODO(region-parity): "Registered as Private Limited Company in
           United Kingdom" is canonical UK Companies House registration copy
           for the legal entity behind Zaveato. Country name stays literal —
           it names the actual jurisdiction, not the shopper's region. -->
      <p class="text-gray-400 text-xs mt-1">
        Powered and Provided by STELLAR INTEGRATION & PLATFORM OPERATIONS LTD. Company Number 14353699, Registered as Private Limited Company in United Kingdom.
      </p>
      <p class="text-gray-400 text-xs mt-0.5">
        UK Registered Trade Mark® No. UK00004333800
      </p>
    </div>
  </footer>

  <!-- Full footer for all other cases -->
  <footer v-else class="bg-gray-900 mt-auto">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <!-- Footer Content -->
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <!-- Brand Section -->
        <div class="col-span-1 md:col-span-1">
          <!-- Zaveato Text Logo -->
          <router-link to="/" @click="handleResetSearch" class="inline-block mb-4 cursor-pointer hover:opacity-80 transition-opacity">
            <span class="text-2xl font-normal text-white tracking-tight" style="font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;">
              Zav<span class="text-rose-500">eat</span>o<span class="text-rose-500">.</span>
            </span>
          </router-link>
          <p class="text-gray-400 text-sm mb-4">
            {{ footerDescription }}
          </p>
          <div class="flex flex-col space-y-2">
            <router-link to="/brochure" class="text-rose-500 hover:text-rose-400 transition-colors text-sm inline-flex items-center font-medium">
              <i class="pi pi-file-pdf mr-2"></i>
              Get Brochure
            </router-link>
            <router-link to="/how-it-works" class="text-rose-500 hover:text-rose-400 transition-colors text-sm inline-flex items-center font-medium">
              <i class="pi pi-question-circle mr-2"></i>
              How It Works
            </router-link>
          </div>
        </div>

        <!-- Quick Links -->
        <div class="col-span-1">
          <h3 class="text-sm font-semibold text-white uppercase tracking-wider mb-4">Quick Links</h3>
          <ul class="space-y-3">
            <li>
              <router-link to="/" @click="handleResetSearch" class="text-gray-400 hover:text-white transition-colors text-sm">
                Home
              </router-link>
            </li>
            <li>
              <router-link to="/about" class="text-gray-400 hover:text-white transition-colors text-sm">
                About Us
              </router-link>
            </li>
            <li>
              <router-link to="/blog" class="text-gray-400 hover:text-white transition-colors text-sm">
                Blog
              </router-link>
            </li>
            <li v-if="brand.features.shop">
              <router-link to="/shops" @click="handleResetSearch" class="text-gray-400 hover:text-white transition-colors text-sm">
                Browse Shops
              </router-link>
            </li>
            <li v-if="brand.features.appointments">
              <router-link to="/services" @click="handleResetSearch" class="text-gray-400 hover:text-white transition-colors text-sm">
                Browse Services
              </router-link>
            </li>
            <li v-if="brand.features.appointments">
              <router-link to="/booking/lookup" class="text-gray-400 hover:text-white transition-colors text-sm">
                Find Your Booking
              </router-link>
            </li>
            <li>
              <router-link to="/faq" class="text-gray-400 hover:text-white transition-colors text-sm">
                FAQ
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Legal -->
        <div class="col-span-1">
          <h3 class="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h3>
          <ul class="space-y-3">
            <li>
              <router-link to="/terms-and-conditions" class="text-gray-400 hover:text-white transition-colors text-sm">
                Terms & Conditions
              </router-link>
            </li>
            <li>
              <router-link to="/privacy-policy" class="text-gray-400 hover:text-white transition-colors text-sm">
                Privacy Policy
              </router-link>
            </li>
            <li>
              <router-link to="/cookies-policy" class="text-gray-400 hover:text-white transition-colors text-sm">
                Cookies Policy
              </router-link>
            </li>
            <li>
              <router-link to="/sitemap" class="text-gray-400 hover:text-white transition-colors text-sm">
                Sitemap
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Contact -->
        <div class="col-span-1">
          <h3 class="text-sm font-semibold text-white uppercase tracking-wider mb-4">Contact</h3>
          <ul class="space-y-3">
            <li>
              <router-link to="/contact" class="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center">
                <i class="pi pi-send mr-2"></i>
                Contact Us
              </router-link>
            </li>
            <li>
              <a :href="`mailto:${brand.footer.email}`" class="text-gray-400 hover:text-white transition-colors text-sm inline-flex items-center">
                <i class="pi pi-envelope mr-2"></i>
                {{ brand.footer.email }}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <!-- Payment methods we accept — single-row band above the divider -->
      <div class="mt-8 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 flex-wrap">
        <span class="text-gray-400 text-xs uppercase tracking-wider">We accept</span>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <span class="zav-pay" title="Visa">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Visa">
              <rect width="48" height="30" rx="4" fill="#1A1F71"/>
              <text x="24" y="20" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="11" font-weight="900" font-style="italic" letter-spacing="1">VISA</text>
            </svg>
          </span>
          <span class="zav-pay" title="Mastercard">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Mastercard">
              <rect width="48" height="30" rx="4" fill="#fff"/>
              <circle cx="20" cy="15" r="8" fill="#EB001B"/>
              <circle cx="28" cy="15" r="8" fill="#F79E1B"/>
              <path d="M24 9.5a8 8 0 0 1 0 11 8 8 0 0 1 0-11Z" fill="#FF5F00"/>
            </svg>
          </span>
          <span class="zav-pay" title="American Express">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="American Express">
              <rect width="48" height="30" rx="4" fill="#1F72CD"/>
              <text x="24" y="19" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="8" font-weight="900" letter-spacing="0.5">AMEX</text>
            </svg>
          </span>
          <span class="zav-pay" title="Apple Pay">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Apple Pay">
              <rect width="48" height="30" rx="4" fill="#000"/>
              <path d="M14.3 12.1c-.4.5-1.1.9-1.8.8-.1-.7.2-1.4.6-1.9.4-.5 1.2-.9 1.8-.9.1.8-.2 1.5-.6 2Zm.6.9c-1 0-1.8.6-2.3.6-.5 0-1.2-.6-2-.6-1 0-2 .6-2.5 1.6-1.1 1.9-.3 4.7.8 6.2.5.7 1.2 1.6 2 1.5.8 0 1.1-.5 2-.5.9 0 1.2.5 2 .5.8 0 1.4-.7 1.9-1.5.6-.8.8-1.6.8-1.6-1.6-.6-2.1-2.9-.7-3.7-.4-.6-1-.9-1.8-.9Z" fill="#fff"/>
              <text x="28" y="19" fill="#fff" font-family="Arial, sans-serif" font-size="7" font-weight="600">Pay</text>
            </svg>
          </span>
          <span class="zav-pay" title="Google Pay">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Google Pay">
              <rect width="48" height="30" rx="4" fill="#fff"/>
              <path d="M14.5 15.4c0-.4 0-.7-.1-1.1H10v2.1h2.5c-.1.6-.4 1.1-.9 1.4v1.2h1.5c.9-.8 1.4-2 1.4-3.6Z" fill="#4285F4"/>
              <path d="M10 20c1.2 0 2.3-.4 3-1.1l-1.5-1.2c-.4.3-1 .5-1.6.5-1.2 0-2.2-.8-2.6-1.9H5.8v1.2c.7 1.5 2.4 2.5 4.2 2.5Z" fill="#34A853"/>
              <path d="M7.4 16.3c-.2-.6-.2-1.2 0-1.7v-1.2H5.8c-.6 1.3-.6 2.8 0 4.1l1.6-1.2Z" fill="#FBBC04"/>
              <path d="M10 12.6c.7 0 1.3.2 1.8.7l1.3-1.3c-.8-.8-1.9-1.2-3.1-1.2-1.8 0-3.4 1-4.2 2.5l1.6 1.2c.4-1.1 1.4-1.9 2.6-1.9Z" fill="#EA4335"/>
              <text x="28" y="19" fill="#5F6368" font-family="Arial, sans-serif" font-size="7" font-weight="600">Pay</text>
            </svg>
          </span>
          <span class="zav-pay" title="Klarna">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Klarna">
              <rect width="48" height="30" rx="4" fill="#FFA8CD"/>
              <text x="24" y="19" text-anchor="middle" fill="#0A0A0A" font-family="Arial, sans-serif" font-size="9" font-weight="900">Klarna.</text>
            </svg>
          </span>
          <span class="zav-pay" title="Clearpay">
            <svg viewBox="0 0 48 30" xmlns="http://www.w3.org/2000/svg" aria-label="Clearpay">
              <rect width="48" height="30" rx="4" fill="#B2FCE4"/>
              <text x="24" y="19" text-anchor="middle" fill="#0A0A0A" font-family="Arial, sans-serif" font-size="8" font-weight="900">Clearpay</text>
            </svg>
          </span>
        </div>
      </div>

      <!-- Follow us — canonical Zaveato socials. Deliberately below the payment
           band and above the © line so it reads as brand-level, not commerce. -->
      <div class="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <span class="text-gray-400 text-xs uppercase tracking-wider">Follow us</span>
        <div class="flex items-center gap-2">
          <a href="https://www.facebook.com/Zaveato" target="_blank" rel="noopener noreferrer" aria-label="Zaveato on Facebook" class="zav-social">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M22 12a10 10 0 1 0-11.6 9.9v-7H8v-2.9h2.4V9.8c0-2.4 1.4-3.7 3.6-3.7 1 0 2.1.2 2.1.2v2.3h-1.2c-1.2 0-1.5.7-1.5 1.5v1.8h2.6l-.4 2.9h-2.2v7A10 10 0 0 0 22 12Z" fill="currentColor"/>
            </svg>
          </a>
          <a href="https://x.com/zaveato" target="_blank" rel="noopener noreferrer" aria-label="Zaveato on X" class="zav-social">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231L18.244 2.25Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" fill="currentColor"/>
            </svg>
          </a>
          <a href="https://www.instagram.com/zaveato" target="_blank" rel="noopener noreferrer" aria-label="Zaveato on Instagram" class="zav-social">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M12 2c2.7 0 3.1 0 4.1.1 1.1 0 1.8.2 2.5.5.7.3 1.2.6 1.8 1.1.5.5.9 1.1 1.1 1.8.3.6.5 1.4.5 2.5 0 1 .1 1.4.1 4.1s0 3.1-.1 4.1c0 1.1-.2 1.8-.5 2.5-.3.7-.6 1.2-1.1 1.8-.5.5-1.1.9-1.8 1.1-.6.3-1.4.5-2.5.5-1 0-1.4.1-4.1.1s-3.1 0-4.1-.1c-1.1 0-1.8-.2-2.5-.5-.7-.3-1.2-.6-1.8-1.1-.5-.5-.9-1.1-1.1-1.8-.3-.6-.5-1.4-.5-2.5 0-1-.1-1.4-.1-4.1s0-3.1.1-4.1c0-1.1.2-1.8.5-2.5.3-.7.6-1.2 1.1-1.8.5-.5 1.1-.9 1.8-1.1.6-.3 1.4-.5 2.5-.5 1 0 1.4-.1 4.1-.1Zm0 1.8c-2.7 0-3 0-4 .1-1 0-1.5.2-1.9.3-.5.2-.8.4-1.2.8-.4.4-.6.7-.8 1.2-.1.4-.3.9-.3 1.9-.1 1-.1 1.3-.1 4s0 3 .1 4c0 1 .2 1.5.3 1.9.2.5.4.8.8 1.2.4.4.7.6 1.2.8.4.1.9.3 1.9.3 1 .1 1.3.1 4 .1s3 0 4-.1c1 0 1.5-.2 1.9-.3.5-.2.8-.4 1.2-.8.4-.4.6-.7.8-1.2.1-.4.3-.9.3-1.9.1-1 .1-1.3.1-4s0-3-.1-4c0-1-.2-1.5-.3-1.9-.2-.5-.4-.8-.8-1.2-.4-.4-.7-.6-1.2-.8-.4-.1-.9-.3-1.9-.3-1-.1-1.3-.1-4-.1Zm0 3.1a5.1 5.1 0 1 1 0 10.2 5.1 5.1 0 0 1 0-10.2Zm0 8.4a3.3 3.3 0 1 0 0-6.6 3.3 3.3 0 0 0 0 6.6Zm5.3-8.6a1.2 1.2 0 1 1 0-2.4 1.2 1.2 0 0 1 0 2.4Z" fill="currentColor"/>
            </svg>
          </a>
        </div>
      </div>

      <!-- Download App — App Store + Google Play badges. Label is "Download App"
           (not "Find us on Zaveato") because the visitor is already on the
           Zaveato website; the CTA here is to take that same experience to a
           native app. Same badge assets used on the WL footer. -->
      <div class="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <span class="text-gray-400 text-xs uppercase tracking-wider">Download App</span>
        <div class="flex items-center gap-3 flex-wrap justify-center">
          <a
            href="https://apps.apple.com/gb/app/zaveato/id6758220942"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Zaveato on the App Store"
            class="zav-app-badge"
          >
            <img src="/badge-app-store.svg" alt="Download on the App Store" />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.zaveato.app"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Zaveato on Google Play"
            class="zav-app-badge"
          >
            <img src="/badge-google-play.svg" alt="Get it on Google Play" />
          </a>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="mt-6 pt-8 border-t border-gray-700">
        <div class="flex flex-col items-center text-center">
          <p class="text-gray-400 text-sm">
            © {{ currentYear }} {{ brand.footer.companyName }}. All rights reserved.
          </p>
          <!-- TODO(region-parity): same as above — literal UK Companies House
               registration; the country names the legal jurisdiction, not the
               shopper's region. -->
          <p class="text-gray-500 text-xs mt-2">
            Powered and Provided by STELLAR INTEGRATION & PLATFORM OPERATIONS LTD. Company Number 14353699, Registered as Private Limited Company in United Kingdom.
          </p>
          <p class="text-gray-500 text-xs mt-1">
            UK Registered Trade Mark® No. UK00004333800
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useServiceStore } from '../stores/service'
import { useServicesStore } from '../stores/services'
import { useBranding } from '@/composables/useBranding'

const route = useRoute()
const serviceStore = useServiceStore()
const servicesStore = useServicesStore()
const { brand } = useBranding()
const currentYear = computed(() => new Date().getFullYear())

// Dynamic brand hover class
const linkHoverClass = computed(() => `hover:text-${brand.value.colors.primary}`)

// Footer description - Zaveato unified platform
const footerDescription = 'Your all-in-one platform for shopping local products and booking professional services.'

// Mobile detection
const isMobile = ref(false)
const MOBILE_BREAKPOINT = 768

function checkMobile() {
  isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

// Show minimal footer only for booking flow on mobile
const isBookingFlowMobile = computed(() => {
  return isMobile.value && route.meta?.isBookingFlow === true
})

// Reset search filters when navigating to home or services
function handleResetSearch() {
  // Clear both stores to ensure all search states are reset
  serviceStore.resetSearch() // For /services page
  servicesStore.clearFilters() // For home page
}
</script>

<style scoped>
/* Canonical Zaveato footer payment badges — mirrors WL footer's
   hf-pay classes but scoped so it doesn't clash with WL styles. */
.zav-pay {
  display: inline-block;
  width: 64px;
  height: 40px;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}
.zav-pay svg {
  width: 100%;
  height: 100%;
  display: block;
}
@media (min-width: 768px) {
  .zav-pay {
    width: 72px;
    height: 46px;
  }
}

/* App-store download badges — sized to sit comfortably next to the
   "Download App" label on desktop and wrap centred on mobile. Same
   PNG/SVG assets used by the WL footer's Find-us-on-Zaveato section. */
.zav-app-badge {
  display: inline-block;
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.zav-app-badge:hover,
.zav-app-badge:focus-visible {
  opacity: 0.9;
  transform: translateY(-1px);
}
.zav-app-badge img {
  height: 40px;
  width: auto;
  display: block;
}
@media (min-width: 768px) {
  .zav-app-badge img {
    height: 44px;
  }
}

/* Follow-us icons — subtle grey that lifts to white on hover. Circle
   background keeps the icon centred at exactly 36px so different SVG
   viewBoxes render at the same visual size. */
.zav-social {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  color: #9ca3af;               /* gray-400 */
  background: rgba(255, 255, 255, 0.04);
  transition: color 0.15s ease, background 0.15s ease, transform 0.15s ease;
}
.zav-social:hover,
.zav-social:focus-visible {
  color: #ffffff;
  background: rgba(255, 255, 255, 0.12);
  transform: translateY(-1px);
}
.zav-social svg {
  width: 18px;
  height: 18px;
  display: block;
}
</style>
